import { css } from "@emotion/react";
import type { Publication } from "@rowan/prisma";
import { useConfig } from "../../hooks/useConfig";
import { FlexColContainer, FlexRowContainer } from "../layout";
import { BookmarkSection, type BookmarkItem } from "./BookmarkSection";
import FrjMenuIcon from "@assets/frjMenuIcon.svg";
import {
  FRJ,
  unknownPublicationTitle,
  type FRJOrUnknownPublication,
} from "./utils";
import { useMemo } from "react";

type PublicationBookmarksRowProps = {
  publication: Publication | FRJOrUnknownPublication;
  bookmarks: BookmarkItem[];
  collection: {
    id: number;
    name: string;
    colour: string;
  };
};
export const PublicationBookmarksRow = ({
  publication,
  bookmarks,
  collection,
}: PublicationBookmarksRowProps) => {
  const { assetBucketUrl } = useConfig();
  const frjArticle = publication.title === FRJ;
  const unknownPublication = publication.title === unknownPublicationTitle;

  const imageSrc = useMemo(() => {
    if (frjArticle) {
      return FrjMenuIcon;
    } else if (unknownPublication) {
      return null;
    } else {
      return "imageUrl" in publication
        ? `${assetBucketUrl ?? ""}/${publication.imageUrl}`
        : null;
    }
  }, [publication, assetBucketUrl, frjArticle, unknownPublication]);

  return (
    <FlexRowContainer
      css={(theme) => css`
        align-items: stretch;
        background-color: ${theme.colours.uIGrey.light._5};
        gap: 0;
        width: 100%;

        @media (max-width: ${theme.breakpoints.values.sm}px) {
          flex-direction: column;
        }
      `}
    >
      <FlexRowContainer
        css={(theme) => css`
          align-items: flex-start;
          border-bottom: 2px solid ${theme.colours.uIGrey.light._15};
          flex: 0 0 33.33%;
          justify-content: flex-start;
          padding: 16px;
          width: 100%;
        `}
      >
        {imageSrc && (
          <img
            src={imageSrc}
            title={publication.title}
            alt={publication.title}
            css={css`
              border-radius: 6px;
              height: 58px;
              margin: 0 10px 0 0;
              width: ${frjArticle ? "58px" : "38px"};
            `}
          />
        )}
        <span
          css={(theme) => css`
            font-weight: ${theme.customTypography.jumbo._5};
          `}
        >
          {frjArticle ? "" : publication.title}
        </span>
      </FlexRowContainer>
      <FlexColContainer
        css={(theme) => css`
          background-color: ${theme.colours.uIGrey.light._5};
          border-bottom: 2px solid ${theme.colours.uIGrey.light._15};
          border-left: 2px solid ${theme.colours.uIGrey.light._15};
          flex: 1;
          max-width: 66.66%;
          min-width: 0;

          @media (max-width: ${theme.breakpoints.values.sm}px) {
            max-width: 100%;
          }
        `}
      >
        <BookmarkSection
          bookmarks={bookmarks}
          collectionId={Number(collection.id)}
          collectionColour={collection.colour}
          publicationTitle={publication.title}
          publicationFilename={
            "filename" in publication ? (publication.filename ?? "") : ""
          }
        />
      </FlexColContainer>
    </FlexRowContainer>
  );
};
